/**
 * ResultsGrid - Visual Grid Display for Test Run Results
 * 
 * Displays cases as columns with criteria stacked vertically.
 * Wraps to new rows (~20 cases per row).
 * Green/red coloring for pass/fail with hover tooltips.
 */

import React, { useState } from "react";

const CASES_PER_ROW = 20;

const ResultsGrid = ({ results, onCaseClick, onCaseEdit, onCriterionClick }) => {
    const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: null });

    if (!results || !results.cases || results.cases.length === 0) {
        return <div className="results-grid-empty">No results to display</div>;
    }

    const cases = results.cases;

    // Group cases into rows
    const rows = [];
    for (let i = 0; i < cases.length; i += CASES_PER_ROW) {
        rows.push(cases.slice(i, i + CASES_PER_ROW));
    }

    const handleMouseEnter = (e, criterion) => {
        const rect = e.target.getBoundingClientRect();
        const tooltipHeight = 200; // Estimated tooltip height
        const viewportHeight = window.innerHeight;

        // Check if tooltip would overflow bottom of viewport
        let yPos = rect.bottom + 8;
        if (rect.bottom + tooltipHeight + 20 > viewportHeight) {
            // Position above the cell instead
            yPos = rect.top - tooltipHeight - 8;
            // Ensure it doesn't go off the top
            if (yPos < 10) yPos = 10;
        }

        setTooltip({
            visible: true,
            x: Math.min(rect.left, window.innerWidth - 420),
            y: yPos,
            content: {
                id: criterion.criterion_id,
                type: criterion.criterion_type,
                description: criterion.description,
                evidence: criterion.evidence,
                result: criterion.result
            }
        });
    };

    const handleMouseLeave = () => {
        setTooltip({ visible: false, x: 0, y: 0, content: null });
    };

    // Handle click on case number to edit
    const handleCaseHeaderClick = (e, caseData) => {
        e.stopPropagation();
        if (onCaseEdit && caseData.case_id) {
            onCaseEdit(caseData.case_id);
        }
    };

    return (
        <div className="results-grid">
            {rows.map((rowCases, rowIndex) => (
                <div key={rowIndex} className="results-row">
                    {rowCases.map((caseData, caseIndex) => {
                        const caseNum = caseData.case_id?.split("_").pop() || `${rowIndex * CASES_PER_ROW + caseIndex + 1}`;

                        return (
                            <div key={`${caseData.case_id}-${caseData.replicate_index ?? 0}`} className="case-column">
                                <div
                                    className={`case-header ${onCaseEdit ? 'editable' : ''}`}
                                    title={`${caseData.case_name}${onCaseEdit ? ' (click to edit)' : ''}`}
                                    onClick={(e) => handleCaseHeaderClick(e, caseData)}
                                    style={{ cursor: onCaseEdit ? 'pointer' : 'default' }}
                                >
                                    {caseNum}
                                </div>
                                <div className="criteria-stack">
                                    {(caseData.results || []).map((criterion, critIndex) => (
                                        <div
                                            key={critIndex}
                                            className={`result-cell ${criterion.result === "PASS" ? "pass" : criterion.result === "REVIEW" ? "review" : "fail"}`}
                                            onMouseEnter={(e) => handleMouseEnter(e, criterion)}
                                            onMouseLeave={handleMouseLeave}
                                            onClick={() => onCriterionClick && onCriterionClick(caseData)}
                                        >
                                            {criterion.criterion_id || "?"}
                                        </div>
                                    ))}
                                    {/* Show empty placeholder if no results */}
                                    {(!caseData.results || caseData.results.length === 0) && !caseData.error && (
                                        <div className="result-cell empty" title="No grading data">
                                            —
                                        </div>
                                    )}
                                    {/* Error indicator if case had an error */}
                                    {caseData.error && (
                                        <div className="result-cell error" title={caseData.error}>
                                            ERR
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}

            {/* Tooltip */}
            {tooltip.visible && tooltip.content && (
                <div
                    className="result-tooltip"
                    style={{
                        left: tooltip.x,
                        top: tooltip.y
                    }}
                >
                    <div className="tooltip-header">
                        <span className={`tooltip-result ${tooltip.content.result === "PASS" ? "pass" : tooltip.content.result === "REVIEW" ? "review" : "fail"}`}>
                            {tooltip.content.result}
                        </span>
                        <span className="tooltip-id">{tooltip.content.id}</span>
                        <span className="tooltip-type">({tooltip.content.type})</span>
                    </div>
                    <div className="tooltip-description">
                        <strong>Description:</strong>
                        <p>{tooltip.content.description}</p>
                    </div>
                    <div className="tooltip-evidence">
                        <strong>Evidence:</strong>
                        <p>{tooltip.content.evidence}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ResultsGrid;
